/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Submission/AdminSubmissionList.tsx.
 */

import React, { useEffect, useState, useMemo } from "react";
import axiosClient from "../../../../api/axiosClient";
import DataTable, { Column, FilterConfig } from "../../../../components/common/DataTable";
import AdminStartReviewModal from "../Materials/AdminStartReviewModal";
import {
  ADMIN_SUBMISSION_LIST_REVIEW_STATUS_FILTER_OPTIONS,
  REVIEW_STATUS,
  sortAdminReviewStatusFilterOptions,
  canAdminApproveDataset,
  formatReviewStatusLabel,
  normalizeReviewStatus,
  reviewStatusBadgeSuffix,
} from "../../../../constants/reviewStatus";
import { adminApproveSubmission } from "../../../../services/submissionService";
import { CheckCircle, Eye, Flag } from "lucide-react";
import "./AdminSubmissionList.css";

interface Material {
  id: number;
  name: string;
  code: string;
}

interface SubmissionRow {
  id: string;
  submissionCode: string;
  contributor: {
    id: string;
    name: string;
    email: string;
  };
  background: {
    materialCode: string;
    materialId?: number;
  } | null;
  createdAt: string;
  submittedDate?: string | null;
  reviewStatus: string;
  currentStage?: string;
}

interface Props {
  onOpenSubmission: (id: string) => void;
}

const AdminSubmissionList: React.FC<Props> = ({ onOpenSubmission }) => {
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    reviewStatus: "",
    materialCode: "",
  });
  
  // Modal state
  const [startReviewModalOpen, setStartReviewModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRow | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Load submissions from API
  const loadSubmissions = () => {
    setLoading(true);
    axiosClient
      .get("/admin/submissions")
      .then((res) => {
        if (res.data.success) {
          // Map the API response to match our interface
          const mappedData: SubmissionRow[] = res.data.data.map((item: any) => ({
            id: item.id,
            submissionCode: item.submissionCode,
            contributor: item.contributor,
            background: item.background,
            createdAt: item.createdAt,
            submittedDate: item.submittedDate ?? item.submitted_date ?? null,
            reviewStatus: item.reviewStatus,
            currentStage: item.currentStage,
          }));
          setRows(mappedData);
        }
      })
      .catch((err) => {
        console.error("Failed to load submissions:", err);
      })
      .finally(() => setLoading(false));
  };

  // Load all materials for filter dropdown
  const loadMaterials = async () => {
    try {
      const res = await axiosClient.get("/materials");
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : res.data?.success
        ? res.data.data || []
        : [];
      setMaterials(list);
    } catch (err) {
      console.error("Failed to fetch materials:", err);
      setMaterials([]);
    }
  };

  useEffect(() => {
    loadSubmissions();
    loadMaterials();
  }, []);

  // Get unique material codes for filter
  const materialCodes = useMemo(() => {
    // Prefer full materials list, fallback to codes derived from rows
    const fromMaterials = (materials || [])
      .map((m) => m?.code)
      .filter(Boolean) as string[];

    if (fromMaterials.length > 0) return Array.from(new Set(fromMaterials)).sort();

    const codes = new Set<string>();
    rows.forEach((row: SubmissionRow) => {
      if (row.background?.materialCode) codes.add(row.background.materialCode);
    });
    return Array.from(codes).sort();
  }, [materials, rows]);

  // Column definitions
  const columns: Column<SubmissionRow>[] = [
    {
      key: "submissionCode",
      label: "Submission Code",
      width: "18%",
    },
    {
      key: "contributor",
      label: "Contributor",
      render: (item) => (
        <div>
          <div style={{ fontWeight: 600, color: "#2d2730" }}>
            {item.contributor?.name || "-"}
          </div>
          {item.contributor?.email && (
            <div style={{ fontSize: "0.85rem", color: "#6b6b6b", marginTop: "2px" }}>
              {item.contributor.email}
            </div>
          )}
        </div>
      ),
      width: "23%",
    },
    {
      key: "background",
      label: "Material",
      render: (item) => (
        <span style={{ fontWeight: 500 }}>
          {item.background?.materialCode || "-"}
        </span>
      ),
      width: "14%",
    },
    {
      key: "reviewStatus",
      label: "Review Status",
      align: "center",
      render: (item) => {
        const status = normalizeReviewStatus(item.reviewStatus);
        const statusClass = `status-badge status-badge-${reviewStatusBadgeSuffix(status)}`;
        return (
          <span className={`status-badge-cell ${statusClass}`}>
            {formatReviewStatusLabel(status)}
          </span>
        );
      },
      width: "22%",
      minWidth: "200px",
    },
    {
      key: "submittedDate",
      label: "Submitted date",
      render: (item) => {
        const raw = item.submittedDate ?? item.createdAt;
        if (!raw) return <span>—</span>;
        const d = new Date(raw);
        return (
          <div style={{ whiteSpace: "nowrap" }}>
            {d.toLocaleDateString()}{" "}
            {d.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        );
      },
      width: "190px",
    },
  ];

  const materialFilterOptions = useMemo(
    () =>
      materials.length > 0
        ? materials
            .filter((m) => !!m?.code)
            .map((m) => ({
              value: m.code,
              label: m.name ? `${m.name} (${m.code})` : m.code,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        : materialCodes.map((code) => ({ value: code, label: code })),
    [materials, materialCodes]
  );

  const reviewStatusFilterOptions = useMemo(
    () => {
      // Base options from constants, plus any extra/legacy statuses currently present in API rows.
      const options = ADMIN_SUBMISSION_LIST_REVIEW_STATUS_FILTER_OPTIONS.map((o) => ({
        value: o.value,
        // Keep dropdown labels identical to the table badge labels.
        label: formatReviewStatusLabel(o.value),
      }));
      const statusesFromRows = Array.from(
        new Set(
          rows
            .map((r) => normalizeReviewStatus(r.reviewStatus))
            .filter(Boolean)
        )
      );
      const extras = statusesFromRows
        .filter(
          (s) =>
            s !== REVIEW_STATUS.ASSIGNED_TO_EXPERT &&
            !options.some((o) => o.value === s)
        )
        .map((s) => ({
          value: s,
          label: formatReviewStatusLabel(s),
        }));
      return sortAdminReviewStatusFilterOptions([...options, ...extras]);
    },
    [rows]
  );

  // Filter configurations: Material first, then Review Status (stable option order)
  const filters: FilterConfig[] = useMemo(
    () => [
      {
        key: "materialCode",
        label: "Material",
        options: materialFilterOptions,
        defaultValue: "",
      },
      {
        key: "reviewStatus",
        label: "Review Status",
        options: reviewStatusFilterOptions,
        defaultValue: "",
      },
    ],
    [materialFilterOptions, reviewStatusFilterOptions]
  );

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  // Filter submissions based on search and filters
  const filteredRows = useMemo(() => {
    let result = [...rows];

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter((row) => {
        const submissionCode = row.submissionCode?.toLowerCase() || "";
        const contributorName = row.contributor?.name?.toLowerCase() || "";
        const contributorEmail = row.contributor?.email?.toLowerCase() || "";
        const materialCode = row.background?.materialCode?.toLowerCase() || "";
        return submissionCode.includes(searchLower) ||
               contributorName.includes(searchLower) ||
               contributorEmail.includes(searchLower) ||
               materialCode.includes(searchLower);
      });
    }

    // Apply review status filter
    if (filterValues.reviewStatus) {
      const want = filterValues.reviewStatus.toUpperCase();
      result = result.filter(
        (row) => normalizeReviewStatus(row.reviewStatus).toUpperCase() === want
      );
    }

    // Apply material code filter
    if (filterValues.materialCode) {
      result = result.filter((row) => {
        return row.background?.materialCode === filterValues.materialCode;
      });
    }

    return result;
  }, [rows, searchTerm, filterValues]);

  // Handle opening modals
  const handleStartReview = (item: SubmissionRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSubmission(item);
    setStartReviewModalOpen(true);
  };

  const handleApproveDataset = async (item: SubmissionRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canAdminApproveDataset(item.reviewStatus)) return;
    if (
      !window.confirm(
        `Approve dataset ${item.submissionCode ?? item.id}? This marks the submission as approved.`
      )
    ) {
      return;
    }
    setApprovingId(item.id);
    try {
      const res = await adminApproveSubmission(item.id);
      if (res.data?.success === false) {
        alert(res.data?.message || "Approval failed.");
        return;
      }
      loadSubmissions();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Approval failed. Please try again.";
      alert(msg);
    } finally {
      setApprovingId(null);
    }
  };

  // Render actions for each row
  const renderActions = (item: SubmissionRow) => {
    const canApprove = canAdminApproveDataset(item.reviewStatus);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          gap: "8px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <button
          className="view-btn icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onOpenSubmission(item.id);
          }}
        >
          <Eye size={16} style={{ stroke: "currentColor" }} />
          <span className="btn-text">View</span>
        </button>
        <button
          className="view-btn icon-btn"
          onClick={(e) => handleStartReview(item, e)}
          style={{ backgroundColor: "#7a1b49", color: "white" }}
        >
          <Flag size={16} style={{ stroke: "currentColor" }} />
          <span className="btn-text">Flag Dataset</span>
        </button>
        <button
          className="view-btn icon-btn"
          type="button"
          disabled={!canApprove || approvingId === item.id}
          title={
            canApprove
              ? undefined
              : "Cannot approve: already approved or returned to contributor for revision."
          }
          onClick={(e) => handleApproveDataset(item, e)}
          style={{
            backgroundColor: "#2e7d32",
            color: "white",
            opacity: canApprove ? 1 : 0.45,
            cursor: canApprove ? "pointer" : "not-allowed",
          }}
        >
          <CheckCircle size={16} style={{ stroke: "currentColor" }} />
          <span className="btn-text">{approvingId === item.id ? "Approving…" : "Approve"}</span>
        </button>
      </div>
    );
  };

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Submissions</span>
      </div>
      <div className="admin-submissions-list">
      {/* Search + filters directly above the table */}
      <div className="admin-submissions-header">
        <div className="admin-submissions-left">
          <div className="data-table-search">
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L11.1 11.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by submission code, contributor name, or email..."
              className="data-table-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {filters.length > 0 && (
          <div className="admin-submissions-right">
            <div className="data-table-filters">
              {filters.map((filter) => (
                <select
                  key={filter.key}
                  className="data-table-filter-select"
                  value={filterValues[filter.key] || ""}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                >
                  <option value="">All {filter.label}</option>
                  {filter.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          </div>
        )}
      </div>

      <DataTable<SubmissionRow>
        data={filteredRows}
        columns={columns}
        loading={loading}
        pagination={true}
        itemsPerPage={10}
        renderActions={renderActions}
        onRowClick={(item) => onOpenSubmission(item.id)}
        emptyMessage="No submissions found"
      />
      </div>

      {/* Start Review Modal */}
      {selectedSubmission && (
        <AdminStartReviewModal
          open={startReviewModalOpen}
          onClose={() => {
            setStartReviewModalOpen(false);
            setSelectedSubmission(null);
          }}
          submissionId={selectedSubmission.id}
          submissionCode={selectedSubmission.submissionCode ?? selectedSubmission.id}
          materialId={selectedSubmission.background?.materialId ?? null}
          onReviewStarted={() => {
            loadSubmissions(); // Refresh the list after review started
            setStartReviewModalOpen(false);
            setSelectedSubmission(null);
          }}
        />
      )}
    </div>
  );
};

/** React component — Admin Submission List. */
export default AdminSubmissionList;